import { useInfiniteQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

function useSessionsList({
  token,
  score = true,
  order_by = '-start_time',
  page_size = 10,
  start_date,
  end_date,
  survey_status = '',
  enabled = true,
}) {
  const fetchSessionsList = async ({ pageParam }) => {
    const params = {
      page: pageParam,
      page_size,
      ...(score !== undefined && { score }),
      ...(order_by && { order_by }),
      ...(start_date && { start_date }),
      ...(end_date && { end_date }),
      ...(survey_status !== '' && { survey_status }),
    };

    try {
      const response = await api.get('/sessions/', {
        params,
        headers: { Authorization: token },
      });
      return response.data;
    } catch (error) {
      throw new Error('Failed to fetch sessions list');
    }
  };

  const queryKey = [
    'sessionsList',
    token,
    score,
    order_by,
    page_size,
    start_date,
    end_date,
    survey_status,
  ];

  const {
    data,
    error,
    isLoading,
    isPending,
    isFetching,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey,
    queryFn: fetchSessionsList,
    gcTime: Infinity,
    staleTime: Infinity,
    enabled: !!token && enabled,
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      // Check if there's a next page based on the API response structure
      if (lastPage?.next) {
        // Handle both URL format and direct page number
        if (typeof lastPage.next === 'number') {
          return lastPage.next;
        }
        if (typeof lastPage.next === 'string') {
          try {
            const url = new URL(lastPage.next);
            return parseInt(url.searchParams.get('page') || '1', 10);
          } catch {
            // If not a valid URL, try parsing as number
            return parseInt(lastPage.next, 10) || undefined;
          }
        }
      }
      return undefined;
    },
  });

  // Flatten the pages data for easier consumption
  const sessions = data?.pages?.flatMap((page) => page.results ?? []) ?? [];

  return {
    sessions,
    data,
    isLoading: isLoading || (isPending && isFetching),
    isPending,
    isFetching,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  };
}

export default useSessionsList;
